import {currencyFormatter, percentageFormatter} from '@dnhb/formatters';
import { useEffect } from 'react';
import {useDispatch, useSelector} from 'shared/hooks';
import {useLocation} from 'react-router-dom';
import analytics from 'shared/utility/analytics';
import {Collapsable, Display, DisplayLink, Heading, LabelText} from 'shared/components';
import {NAVIGATE, SYNC} from 'shared/wizard/actions';

const WizardFinal = () => {
  const dispatch = useDispatch();
  const {
    pages,
    input: {
      applicant_dob,
      applicant_is_entrepreneur,
      applicant_has_partner,
      applicant_pension_income,
      applicant_pension_income_period,
      partner_dob,
      partner_is_entrepreneur,
      partner_pension_income,
      partner_pension_income_period,
      has_student_loan,
      student_loan_monthly_payment,
      partner_student_loan_monthly_payment,
      has_total_credits,
      total_credits,
      has_partner_alimony,
      partner_alimony,
      has_ground_rent,
      ground_rent,
      purchase_price,
      has_surplus_value,
      surplus_value,
      has_own_resources,
      own_resources,
      has_private_lease,
      private_lease,
      has_partner_private_lease,
      partner_private_lease,
      energy_label,
      new_regulations,
    },
    output: {
      data: {
        applicant_income,
        show_applicant_pension_income,
        partner_income,
        show_partner_pension_income,
        possibility: { interest },
      }
    },
    meta: { energy_labels, energy_labels_next_year },
  } = useSelector(state => state);
  const location = useLocation();
  const currentPageIndex = pages.indexOf(location.state?.page);

  useEffect(() => {
    dispatch({ type: SYNC, syncing: false, recalculate: false});
    analytics.send('Bereken', 'Resultaat');
  }, []);

  const hasExpenses = has_student_loan || has_total_credits || has_partner_alimony || has_ground_rent || has_private_lease;

  return (
    <>
      <Heading>Uitgangspunten</Heading>
      <LabelText css={{ marginBottom: '24px' }}>
        Controleer of je gegevens kloppen en pas ze aan als dat nodig is.
      </LabelText>
      <Collapsable title="Woning en (spaar)geld" openByDefault>
        <Display label="Koopprijs" value={currencyFormatter(purchase_price)} />
        {has_surplus_value && <Display label="Overwaarde" value={currencyFormatter(surplus_value)} />}
        {has_own_resources && <Display label="Eigen inleg" value={currencyFormatter(own_resources)} />}
        <Display label="Energielabel" value={(new_regulations ? energy_labels_next_year : energy_labels).find(({value}) => value === energy_label)?.label} />
        <DisplayLink
          onClick={() => {
            dispatch({ type: NAVIGATE, recalculate: currentPageIndex, index: 0 });
          }}
        >
          Gegevens aanpassen
        </DisplayLink>
      </Collapsable>
      <Collapsable title="Uw gegevens">
        <Display label="Geboortedatum" value={applicant_dob} />
        <Display label="Ondernemer" value={applicant_is_entrepreneur} />
        <Display label="Bruto jaarinkomen" value={currencyFormatter(applicant_income)} />
        {show_applicant_pension_income && (
          <Display
            label="Pensioen jaarinkomen"
            value={currencyFormatter(
              (applicant_pension_income_period === 'monthly' ? 12 : 1) * applicant_pension_income
            )}
          />
        )}
        <DisplayLink
          onClick={() => {
            dispatch({ type: NAVIGATE, recalculate: currentPageIndex, index: 1 });
          }}
        >
          Gegevens aanpassen
        </DisplayLink>
      </Collapsable>
      {applicant_has_partner && (
        <Collapsable title="Uw partners gegevens">
          <Display label="Geboortedatum" value={partner_dob} />
          <Display label="Ondernemer" value={partner_is_entrepreneur} />
          <Display label="Bruto jaarinkomen" value={currencyFormatter(partner_income)} />
          {show_partner_pension_income && (
            <Display
              label="Pensioen jaarinkomen"
              value={currencyFormatter((partner_pension_income_period === 'monthly' ? 12 : 1) * partner_pension_income)}
            />
          )}
          <DisplayLink
            onClick={() => {
              dispatch({ type: NAVIGATE, recalculate: currentPageIndex, index: 2 });
            }}
          >
            Gegevens aanpassen
          </DisplayLink>
        </Collapsable>
      )}
      {hasExpenses ? (
        <Collapsable title="Financi&#xEB;le verplichtingen">
          {has_student_loan && (
            <>
              {student_loan_monthly_payment > 0 && (
                <>
                  <Display label="Maandelijkse aflossing studieschuld" value={currencyFormatter(student_loan_monthly_payment)} />
                </>
              )}
              {applicant_has_partner && partner_student_loan_monthly_payment > 0 && (
                <>
                  <Display label="Partner: maandelijkse aflossing studieschuld"
                           value={currencyFormatter(partner_student_loan_monthly_payment)} />
                </>
              )}
            </>
          )}
          {has_total_credits && <Display label="Totale kredieten" value={currencyFormatter(total_credits)} />}
          {has_partner_alimony && (
            <Display label="Maandelijkse partneralimentatie" value={currencyFormatter(partner_alimony)} />
          )}
          {has_ground_rent && <Display label="Jaarlijkse erfpacht" value={currencyFormatter(ground_rent)} />}
          {has_private_lease && (
            <>
              <Display label="Maandelijkse private lease" value={currencyFormatter(private_lease)} />
            </>
          )}
          {has_partner_private_lease && (
            <>
              <Display label="Maandelijkse private lease (Partner)" value={currencyFormatter(partner_private_lease)} />
            </>
          )}
          <DisplayLink
            onClick={() => {
              dispatch({type: NAVIGATE, recalculate: currentPageIndex, index: 3});
            }}
          >
            Gegevens aanpassen
          </DisplayLink>
        </Collapsable>
      ) : null}
      <Collapsable title="Berekening op basis van">
        <Display
          label="Hypotheekvorm"
          value="Annuïteiten"
          infoText={`
		We gaan uit van de meest populaire hypotheekvorm: de annuïteitenhypotheek. 
		Hiermee betaal je elke maand een vast bedrag: de annuïteit. 
		Binnen dit vaste maandbedrag los je steeds méér af en betaal je steeds minder rente. 
		Aan het einde van de looptijd is de gehele hypothecaire lening afbetaald.
          `}
        />
        <Display
          label="Looptijd"
          value="30 jaar"
          infoText={`
		Deze berekening gaat uit van een looptijd van 30 jaar: 
		het maximum aantal jaar dat je de hypotheekrente mag aftrekken bij je aangifte inkomstenbelasting. 
		Let op: als je al eerder gebruik maakte van deze regeling kan het aantal jaar afwijken van wat je hiernaast ziet.
          `}
        />
        <Display
          label="Rentevasteperiode"
          value="10 jaar"
          infoText={`
		Deze berekening gaat er vanuit dat de rente bij de start van de hypotheek 10 jaar vast staat. 
		Dit biedt je volgens de huidige regels meestal de hoogste hypotheek. Bespreek met je adviseur wat het beste past bij jouw situatie.
          `}
        />
        {interest && (
          <Display
            label="Rente"
            value={percentageFormatter(interest)}
            infoText={`
		Deze berekening vergelijkt wat je nodig hebt aan hypotheek met de waarde van de woning. 
		Voor die verhouding selecteren we de hypotheekaanbieder met het gunstigste rentepercentage, 
		op basis van de nu geldende rentepercentages van alle aangesloten geldverstrekkers.
            `}
          />
        )}
      </Collapsable>
    </>
  );
};

export default WizardFinal;
